import { useGlobalForm } from '@workspace/core-ts/contexts';
import { cn } from '@workspace/utils';
import { cva } from 'class-variance-authority';
import { useSearchParams } from 'react-router-dom';
import type { ChildrenProps } from '../types';
import { Button } from './form';
import { ModalRappeler } from './modal-rappeler';
import { Stepper } from './stepper';

const backButtonVariants = cva(['px-2 lg:px-6 py-2 lg:py-4 whitespace-nowrap transition-opacity duration-200 flex justify-between md:w-auto order-1'], {
    variants: {
        state: {
            visible: 'opacity-100',
            hidden: 'opacity-0',
        },
    },
    defaultVariants: {
        state: 'visible',
    },
});

export function Header({ className }: { className?: string }) {
    const { others, initialState, meta, disableNavigation, steps, config } = useGlobalForm();
    const whitelabel = others?.whitelabel;

    const disablePreviousButton = (steps[0].index === meta.step && meta.subStep === 0) || disableNavigation;

    // Configuration par défaut si non fournie
    const headerConfig = config?.header ?? {
        showBackButton: true,
        showLogo: true,
        showPhoneButton: false,
        showStepper: true,
    };

    // Numéro de téléphone affiché dans le bouton (configurable par app)
    const phoneNumber = headerConfig.phoneNumber ?? '02 19 01 19 46';

    // Gestion de la modal rappeler
    const showModalRappeler = config?.features?.enableModalRappeler !== false;

    // Récupération des codes MAE
    const codeAdherentMAE = initialState?.DemandeTarif?.Police?.CodeAdherentMAE;
    const codeVendeurMAE = initialState?.DemandeTarif?.Police?.CodeVendeurMAE;
    const showMAECodes = codeAdherentMAE || codeVendeurMAE;

    return (
        <>
            {showModalRappeler && <ModalRappeler />}
            <header className={cn('w-full mx-auto padding-root pt-3 pb-4 bg-white', className)}>
                {/* Header principal */}
                <div className="flex items-center justify-between gap-1 md:gap-3 flex-wrap">
                    {headerConfig.showBackButton !== false && (
                        <BackButton
                            className={backButtonVariants({
                                state: disablePreviousButton ? 'hidden' : 'visible',
                            })}
                        />
                    )}

                    {headerConfig.showLogo !== false && (
                        <img
                            alt={whitelabel?.name}
                            className="order-2 w-24 sm:w-28 h-auto self-center xl:ml-6"
                            src={whitelabel?.logo_url}
                        />
                    )}

                    {headerConfig.showStepper !== false && (
                        <Stepper className="w-full md:w-auto flex justify-center md:justify-start md:flex-grow order-4 md:order-3 mx-4 mt-2 sm:mt-0 sm:mx-0 xl:mr-12" />
                    )}

                    <ButtonPhone
                        phoneNumber={phoneNumber}
                        className={cn(
                            'order-3 md:order-4 transition-opacity duration-200',
                            headerConfig.showPhoneButton && initialState.source === 'site'
                                ? 'opacity-100'
                                : 'opacity-0 h-0 cursor-not-allowed pointer-events-none',
                        )}
                    />
                </div>

                {/* Bloc MAE mobile complètement en dehors du header, centré */}
                {showMAECodes && (
                    <div className="w-full flex justify-center px-6 mt-2">
                        <MAECodesMobile
                            codeAdherentMAE={codeAdherentMAE}
                            codeVendeurMAE={codeVendeurMAE}
                        />
                    </div>
                )}
            </header>
        </>
    );
}

function BackButton({ className }: ChildrenProps) {
    const { meta, actions } = useGlobalForm();

    return (
        <Button
            arrow={false}
            className={className}
            disabled={meta.step === 0 && meta.subStep === 0}
            onClick={actions.goPreviousQuestion}
            size="xs"
            type="button"
            variant="outline"
        >
            <span className="hidden lg:block">Question précédente</span>
            <svg
                className="w-6 h-6 lg:hidden"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M15.75 19.5 8.25 12l7.5-7.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </Button>
    );
}

function MAECodesMobile({ codeAdherentMAE, codeVendeurMAE }: { codeAdherentMAE?: string; codeVendeurMAE?: string }) {
    if (!codeAdherentMAE && !codeVendeurMAE) return null;

    return (
        <div className="flex flex-col sm:flex-row gap-2 px-3 py-2 bg-primary-50 rounded-lg border border-primary-200">
            {codeAdherentMAE && (
                <div className="flex items-center justify-center gap-2">
                    <span className="text-xs text-primary-600 font-medium uppercase tracking-wide">Code adhérent</span>
                    <span className="text-sm font-bold text-primary">{codeAdherentMAE || 'N/A'}</span>
                </div>
            )}
            {codeAdherentMAE && codeVendeurMAE && <span className="hidden sm:inline-block border-l border-primary h-6" />}

            {codeVendeurMAE && (
                <div className="flex items-center justify-center gap-2">
                    <span className="text-xs text-primary-600 font-medium uppercase tracking-wide">Code Vendeur</span>
                    <span className="text-sm font-bold text-primary">{codeVendeurMAE}</span>
                </div>
            )}
        </div>
    );
}

function ButtonPhone({ className, phoneNumber }: ChildrenProps & { phoneNumber: string }) {
    const [, setSearchParams] = useSearchParams();

    return (
        <div
            className={cn('flex flex-col items-center', className)}
            onClick={() => setSearchParams({ modal: 'rappeler' })}
        >
            <button
                className={
                    'inline-flex items-center gap-3 rounded-full font-medium group cursor-pointer px-2.5 lg:px-4 py-2.5 lg:py-2 transition-all duration-200 ease-in-out text-left bg-white hover:bg-primary hover:text-white border border-primary hover:border-primary text-primary'
                }
                type="button"
            >
                <svg
                    className="stroke-primary group-hover:stroke-white"
                    fill="none"
                    height="21"
                    viewBox="0 0 22 21"
                    width="22"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M13.1674 4.76358C14.023 4.93052 14.8093 5.34898 15.4258 5.96541C16.0422 6.58185 16.4607 7.3682 16.6276 8.22384M13.1674 1.25952C14.945 1.45701 16.6027 2.25307 17.8683 3.51702C19.1338 4.78096 19.9319 6.43765 20.1317 8.21508M19.2557 15.2057V17.8337C19.2566 18.0777 19.2067 18.3192 19.1089 18.5427C19.0112 18.7663 18.8678 18.9669 18.6881 19.1318C18.5083 19.2968 18.296 19.4223 18.0649 19.5005C17.8338 19.5787 17.5889 19.6077 17.3459 19.5857C14.6503 19.2928 12.0609 18.3717 9.78594 16.8964C7.66933 15.5514 5.87483 13.7569 4.52985 11.6403C3.04937 9.35495 2.12803 6.753 1.84048 4.04525C1.81859 3.803 1.84738 3.55885 1.92502 3.32834C2.00266 3.09783 2.12744 2.88601 2.29143 2.70637C2.45541 2.52672 2.65501 2.38319 2.8775 2.28492C3.1 2.18664 3.34052 2.13576 3.58375 2.13554H6.2118C6.63693 2.13135 7.04908 2.2819 7.37143 2.55912C7.69378 2.83634 7.90432 3.22131 7.96382 3.64228C8.07475 4.48331 8.28046 5.3091 8.57704 6.10388C8.6949 6.41743 8.72041 6.75819 8.65054 7.08579C8.58067 7.41339 8.41836 7.71409 8.18283 7.95227V7.95227C7.52568 8.60942 7.37609 9.63475 7.92307 10.3861C8.78338 11.5678 9.82337 12.6078 11.0051 13.4681C11.7564 14.0151 12.7818 13.8655 13.4389 13.2084V13.2084C13.6771 12.9728 13.9778 12.8105 14.3054 12.7406C14.633 12.6708 14.9738 12.6963 15.2873 12.8142C16.0821 13.1107 16.9079 13.3164 17.7489 13.4274C18.1744 13.4874 18.5631 13.7017 18.8409 14.0296C19.1187 14.3575 19.2663 14.7761 19.2557 15.2057Z"
                        strokeLinecap="round"
                        strokeWidth="1.5"
                    />
                </svg>

                <div className="flex-col hidden lg:flex">
                    <span className="flex text-[10px] text-muted-foreground group-hover:text-inherit whitespace-nowrap">Un conseiller vous aide</span>
                    <span className="text-xs font-semibold">{phoneNumber}</span>
                </div>
            </button>

            <div className="text-center hidden lg:block">
                <p className="text-xs text-muted-foreground mt-2">
                    ou <span className="text-primary font-semibold hover:underline cursor-pointer">rappelez-moi</span>
                </p>
            </div>
        </div>
    );
}
